import { useState, useEffect } from 'react';
import { DeviceInfo } from '../types';
import { getDeviceInfo, throttle } from '../utils/device';

/**
 * 设备信息Hook
 */
export const useDevice = (): DeviceInfo => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(getDeviceInfo());

  useEffect(() => {
    const handleResize = throttle(() => {
      setDeviceInfo(getDeviceInfo());
    }, 100);

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return deviceInfo;
};

/**
 * 安全区域Hook
 */
export const useSafeArea = () => {
  const [safeArea, setSafeArea] = useState({
    top: 0,
    right: 0,
    bottom: 0,
    left: 0
  });

  useEffect(() => {
    const updateSafeArea = () => {
      const computedStyle = getComputedStyle(document.documentElement);
      setSafeArea({
        top: parseInt(computedStyle.getPropertyValue('env(safe-area-inset-top)') || '0'),
        right: parseInt(computedStyle.getPropertyValue('env(safe-area-inset-right)') || '0'),
        bottom: parseInt(computedStyle.getPropertyValue('env(safe-area-inset-bottom)') || '0'),
        left: parseInt(computedStyle.getPropertyValue('env(safe-area-inset-left)') || '0')
      });
    };

    updateSafeArea();
    
    // 监听设备旋转
    window.addEventListener('orientationchange', updateSafeArea);
    
    return () => {
      window.removeEventListener('orientationchange', updateSafeArea);
    };
  }, []);

  return safeArea;
};

/**
 * 触摸手势Hook
 */
export const useTouch = () => {
  const [isLongPress, setIsLongPress] = useState(false);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY });
    
    // 长按检测
    const timer = setTimeout(() => {
      setIsLongPress(true);
    }, 500);

    const handleTouchEnd = () => {
      clearTimeout(timer);
      setIsLongPress(false);
      setTouchStart(null);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchStart) return;
      
      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStart.x);
      const deltaY = Math.abs(touch.clientY - touchStart.y);
      
      // 如果移动距离超过阈值，取消长按
      if (deltaX > 10 || deltaY > 10) {
        clearTimeout(timer);
        setIsLongPress(false);
      }
    };

    document.addEventListener('touchend', handleTouchEnd, { once: true });
    document.addEventListener('touchmove', handleTouchMove);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  };

  return {
    isLongPress,
    touchStart,
    handleTouchStart
  };
}; 